import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, KeyRound, Loader2, ShieldOff } from 'lucide-react';
import Modal from '../../../components/Modal/Modal';
import { tempAccessApi } from '../../../services/tempAccessApi';
import { TEMP_ACCESS_QUERY_KEY } from '../../../hooks/useTempAccess';
import { formatDateTime } from '../../../utils/format';

function useRemaining(expiresAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

function CopyField({ label, value, mono = true, big = false }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard bloqueado — o valor continua visível */
    }
  };
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
        <span className={`flex-1 break-all text-gray-900 ${mono ? 'font-mono' : ''} ${big ? 'text-2xl tracking-widest' : 'text-sm'}`}>
          {value}
        </span>
        <button
          type="button"
          onClick={copy}
          title={t('managedDevices.tempAccess.copy')}
          className="p-1.5 rounded text-theme-primary hover:bg-theme-primary/10"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>
    </div>
  );
}

/**
 * Acesso temporário `automais-temp` (8h). Abrir = concessão (ou reexibição da ativa) — o usuário
 * clicou no ícone justamente para isso. Exceção: concessão anterior que a nuvem não conseguiu
 * revogar (vulnerável) — aí mostra o problema e deixa escolher entre tentar revogar ou gerar outra.
 */
const TempAccessModal = ({ device, current, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState(current?.status === 'vulnerable' ? 'vulnerable' : 'loading');
  const [access, setAccess] = useState(current?.status === 'vulnerable' ? current : null);
  const [error, setError] = useState(null);
  const remaining = useRemaining(phase === 'granted' ? access?.expiresAt : null);

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [TEMP_ACCESS_QUERY_KEY] }),
    [queryClient]
  );

  const grant = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      setAccess(await tempAccessApi.grant(device.id));
      setPhase('granted');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    } finally {
      refresh();
    }
  }, [device.id, refresh]);

  const revoke = async () => {
    setPhase('revoking');
    setError(null);
    try {
      const result = await tempAccessApi.revoke(device.id);
      setAccess(result);
      setPhase(result && result.status !== 'revoked' ? 'vulnerable' : 'revoked');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    } finally {
      refresh();
    }
  };

  useEffect(() => {
    if (phase === 'loading') grant();
    // só na abertura
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSsh = access?.protocol === 'ssh';
  const endpoint = access?.host ? `${access.host}:${access.port ?? (isSsh ? 22 : 8291)}` : '';
  const sshCommand = access?.host
    ? `ssh ${access.username}@${access.host}${access.port && access.port !== 22 ? ` -p ${access.port}` : ''}`
    : '';

  return (
    <Modal isOpen onClose={onClose} title={t('managedDevices.tempAccess.title', { name: device.name })}>
      {(phase === 'loading' || phase === 'revoking') && (
        <div className="flex items-center justify-center gap-2 py-10 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin text-theme-primary" />
          {phase === 'loading' ? t('managedDevices.tempAccess.granting') : t('managedDevices.tempAccess.revoking')}
        </div>
      )}

      {phase === 'error' && (
        <div className="space-y-4">
          <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertTriangle size={18} className="shrink-0" />
            <span>{error}</span>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('common.buttons.close')}
            </button>
            <button onClick={grant} className="px-4 py-2 rounded-lg bg-theme-primary text-white hover:bg-theme-primary/90">
              {t('managedDevices.tempAccess.retry')}
            </button>
          </div>
        </div>
      )}

      {phase === 'vulnerable' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 space-y-2">
            <div className="flex items-center gap-2 font-semibold">
              <AlertTriangle size={18} />
              {t('managedDevices.tempAccess.vulnerableTitle')}
            </div>
            <p>{t('managedDevices.tempAccess.vulnerableBody', { user: access?.username || 'automais-temp' })}</p>
            {access?.expiresAt && (
              <p className="text-xs">
                {t('managedDevices.tempAccess.expiredAt', { when: formatDateTime(access.expiresAt) })}
                {access.revokeAttempts > 0 && ` · ${t('managedDevices.tempAccess.attempts', { n: access.revokeAttempts })}`}
              </p>
            )}
            {access?.lastRevokeError && <p className="font-mono text-xs break-all">{access.lastRevokeError}</p>}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button onClick={grant} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('managedDevices.tempAccess.newAccess')}
            </button>
            <button onClick={revoke} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2">
              <ShieldOff size={16} />
              {t('managedDevices.tempAccess.revokeRetry')}
            </button>
          </div>
        </div>
      )}

      {phase === 'revoked' && (
        <div className="space-y-4">
          <div className="flex gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            <Check size={18} className="shrink-0 text-green-600" />
            {t('managedDevices.tempAccess.revokedOk')}
          </div>
          <div className="flex justify-end">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('common.buttons.close')}
            </button>
          </div>
        </div>
      )}

      {phase === 'granted' && access && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg bg-theme-primary/10 p-3 text-sm text-gray-700">
            <KeyRound size={18} className="mt-0.5 shrink-0 text-theme-primary" />
            <div>
              {isSsh ? t('managedDevices.tempAccess.introSsh') : t('managedDevices.tempAccess.introWinbox')}
              {access.reused && <div className="mt-1 text-xs text-gray-500">{t('managedDevices.tempAccess.reused')}</div>}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <CopyField label={t('managedDevices.tempAccess.user')} value={access.username} />
            <CopyField label={t('managedDevices.tempAccess.password')} value={access.password || '—'} big />
          </div>
          {isSsh ? (
            <CopyField label={t('managedDevices.tempAccess.command')} value={sshCommand} />
          ) : (
            <CopyField label={t('managedDevices.tempAccess.winboxAddress')} value={endpoint} />
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
            <span>{t('managedDevices.tempAccess.validUntil', { when: formatDateTime(access.expiresAt) })}</span>
            <span className="font-mono text-gray-900">{remaining}</span>
          </div>

          {access.warnings?.length > 0 && (
            <ul className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 space-y-1">
              {access.warnings.map((w) => (
                <li key={w} className="flex gap-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  {w}
                </li>
              ))}
            </ul>
          )}

          <p className="text-xs text-gray-500">{t('managedDevices.tempAccess.autoRevokeNote')}</p>

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('common.buttons.close')}
            </button>
            <button onClick={revoke} className="px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-2">
              <ShieldOff size={16} />
              {t('managedDevices.tempAccess.revokeNow')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TempAccessModal;
